/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type {
  U3400DashboardLayout,
  U3400InitLU,
  U3400KpiUserLuPreferenceEffective,
  U3400TileLogicalUnitCode,
} from './U3400Service';
import { U340X_KPI_DEFAULT_MONTHS } from '../u340xKpiShared/types/u340xKpiTypes';

export interface U3400ContextType {
  initLU: U3400InitLU;
  setInitLU: React.Dispatch<React.SetStateAction<U3400InitLU>>;
  layout: U3400DashboardLayout;
  setLayout: React.Dispatch<React.SetStateAction<U3400DashboardLayout>>;
  monthPreferences: U3400KpiUserLuPreferenceEffective[];
  setMonthPreferences: React.Dispatch<React.SetStateAction<U3400KpiUserLuPreferenceEffective[]>>;
  getMonthsTile: (code: U3400TileLogicalUnitCode) => number;
  upsertMonthPreferenceLocal: (pref: U3400KpiUserLuPreferenceEffective) => void;
}

const U3400Context = createContext<U3400ContextType | undefined>(undefined);

interface U3400ContextProviderProps {
  initLU: U3400InitLU;
  children: React.ReactNode;
}

export const U3400ContextProvider: React.FC<U3400ContextProviderProps> = ({ initLU, children }) => {
  const [initLUState, setInitLU] = useState(initLU);
  const [layout, setLayout] = useState(initLU.currentLayout);
  const [monthPreferences, setMonthPreferences] = useState(initLU.monthPreferences);

  const getMonthsTile = useCallback(
    (code: U3400TileLogicalUnitCode) => {
      const pref = monthPreferences.find((p) => p.logicalUnitCode === code);
      return pref?.monthsTile ?? U340X_KPI_DEFAULT_MONTHS;
    },
    [monthPreferences],
  );

  const upsertMonthPreferenceLocal = useCallback((pref: U3400KpiUserLuPreferenceEffective) => {
    setMonthPreferences((prev) => {
      const idx = prev.findIndex((p) => p.logicalUnitCode === pref.logicalUnitCode);
      if (idx < 0) return [...prev, pref];
      const next = [...prev];
      next[idx] = pref;
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      initLU: initLUState,
      setInitLU,
      layout,
      setLayout,
      monthPreferences,
      setMonthPreferences,
      getMonthsTile,
      upsertMonthPreferenceLocal,
    }),
    [
      initLUState,
      layout,
      monthPreferences,
      getMonthsTile,
      upsertMonthPreferenceLocal,
    ],
  );

  return <U3400Context.Provider value={value}>{children}</U3400Context.Provider>;
};

export function useU3400Context(): U3400ContextType {
  const ctx = useContext(U3400Context);
  if (!ctx) {
    throw new Error('useU3400Context must be used within U3400ContextProvider');
  }
  return ctx;
}
